import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { PermissionFlags } from 'src/app/enums/permissionFlags.enum';
import { RolePermission } from 'src/app/models/rolePermission.model';
import { UserRole } from 'src/app/models/userRole.model';
import { ToastService } from 'src/app/services/toast.service';
import { UserRoleService } from 'src/app/services/userRole.service';
import { PERMISSION_NAMES } from 'src/app/shared/constants/permissionNames.constants';
import { ObjectIdLookup } from 'src/app/models/objectIdLookup.model';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { usersRouteConfig } from 'src/app/shared/constants/usersRouteConfig.constants';

const ALL_PERMISSIONS = PermissionFlags.ReadMy | PermissionFlags.CreateMy | PermissionFlags.UpdateMy | PermissionFlags.DeleteMy | PermissionFlags.ReadHierarchy |
  PermissionFlags.CreateHierarchy | PermissionFlags.UpdateHierarchy | PermissionFlags.DeleteHierarchy;

@Component({
  standalone: false,
  selector: 'app-role-permission-list',
  templateUrl: './role-permission-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./role-permission-list.component.scss']
})
export class RolePermissionListComponent implements OnInit {
  roles: UserRole[] = [];
  permissionNames = PERMISSION_NAMES;
  permissionsList = [
    PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy,
    PermissionFlags.CreateMy | PermissionFlags.UpdateMy,
    PermissionFlags.DeleteMy,
    ALL_PERMISSIONS
  ];
  objects: ObjectIdLookup[] = [];
  objectNamesById = new Map<string, string>();
  isEditingRole: { [roleId: number]: boolean } = {};
  editState: { [roleId: number]: UserRole } = {};
  displayedColumns = ['module', ...this.permissionNames];
  cloneSources: { [roleId: string]: string | null } = {};
  tabs = usersRouteConfig;
  readonly piiObjectId = RolePermissionObjects.Pii;
  readonly readFlag = PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy;

  constructor(private userRoleService: UserRoleService,
    private toastService: ToastService
  ) { }

  ngOnInit() {
    forkJoin({
      roles: this.userRoleService.getUserRoles(),
      objects: this.userRoleService.getObjectIds()
    }).subscribe(({ roles, objects }) => {
      this.roles = roles;
      this.objects = objects;
      this.objectNamesById = new Map(objects.map(obj => [obj.id, obj.name]));
      this.objectNamesById.set(RolePermissionObjects.Pii, 'Can View PII');
      this.roles.forEach(role => {
        this.isEditingRole[role.id] = false;
      });
    });
  }

  initializeRolePermissions(): RolePermission[] {
    return this.objects.map(obj => ({
      objectId: obj.id,
      permissions: 0
    }));
  }

  addRole() {
    const newRole: UserRole = { name: '', description: '', requireMfa: false, rolePermissions: this.initializeRolePermissions() };
    this.roles.push(newRole);
    this.isEditingRole[newRole.id] = true;
    this.editState[newRole.id] = JSON.parse(JSON.stringify(newRole));
  }

  clonePermissions(role: UserRole, cloneFromRoleId: string) {
    if (cloneFromRoleId) {
      const clonedPermissions = this.roles.find(r => r.id === cloneFromRoleId)?.rolePermissions;
      if (clonedPermissions) {
        role.rolePermissions = clonedPermissions.map(perm => ({
          ...perm,
          id: undefined,
          roleId: role.id
        }));
      }
    } else {
      role.rolePermissions = this.initializeRolePermissions();
    }
    this.cloneSources[role.id] = cloneFromRoleId;
  }

  editRole(userRole: UserRole) {
    this.isEditingRole[userRole.id] = true;
    this.editState[userRole.id] = JSON.parse(JSON.stringify(userRole));
  }

  cancelEdit(userRole: UserRole) {
    if (!userRole.id) {
      this.roles = this.roles.filter(r => r !== userRole);
    } else {
      const originalRole = this.editState[userRole.id];
      if (originalRole) {
        Object.assign(userRole, originalRole);
      }
    }
    delete this.editState[userRole.id];
    this.isEditingRole[userRole.id] = false;
  }

  saveRole(userRole: UserRole) {
    const serviceCall: Observable<UserRole | undefined> = !userRole.id
      ? this.userRoleService.create(userRole)
      : this.userRoleService.update(userRole).pipe(map(() => undefined));

    serviceCall.subscribe({
      next: (savedUserRole?: UserRole) => {
        this.toastService.showSuccess(!!userRole.id ? 'Role updated successfully' : 'Role saved successfully', true);
        delete this.editState[userRole.id];
        delete this.cloneSources[userRole.id];
        this.isEditingRole[userRole.id] = false;
        if (savedUserRole)
          userRole.id = savedUserRole.id;
      },
      error: (error: HttpErrorResponse) => {
        this.toastService.displayErrorMessage(error.error);
      }
    });
  }

  isDisabled(userRole: UserRole): boolean {
    return !userRole.name || !userRole.description;
  }

  togglePermission(rolePermission: RolePermission, flag: number) {
    if (flag === ALL_PERMISSIONS) {
      rolePermission.permissions = rolePermission.permissions === ALL_PERMISSIONS ? 0 : ALL_PERMISSIONS;
    } else {
      if (rolePermission.permissions !== ALL_PERMISSIONS) {
        rolePermission.permissions ^= flag;
      }
    }
  }

  hasPermission(rolePermission: RolePermission, flag: number) {
    return rolePermission.permissions === ALL_PERMISSIONS || (rolePermission.permissions & flag) === flag;
  }

  isPermissionDisabled(rolePermission: RolePermission, flag: number): boolean {
    const readPermissions = PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy;

    // "Can View PII" (GLOBAL_Pii) is view-only — only the Read flag is meaningful.
    if (rolePermission.objectId === this.piiObjectId) {
      return flag !== readPermissions;
    }

    return ((this.hasPermission(rolePermission, ALL_PERMISSIONS) && flag !== ALL_PERMISSIONS) ||
      ((flag === (PermissionFlags.CreateMy | PermissionFlags.UpdateMy) || flag === PermissionFlags.DeleteMy) &&
        !this.hasPermission(rolePermission, readPermissions))
    );
  }

  getObjectName(objectId: string): string {
    return this.objects.find(obj => obj.id === objectId)?.name ?? objectId;
  }

    public trackById = trackById;
    public trackByIndex = trackByIndex;
}